import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  const openModal = (type: ModalType) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);
  return <footer className="bg-navy-900 py-12 px-4 border-t border-gold-500/10 relative z-50" data-id="element-21">
      <div className="max-w-4xl mx-auto text-center" data-id="element-22">
        <p className="font-lato text-cream/40 text-sm mb-6" data-id="element-23">
          © {new Date().getFullYear()} El Método Sueño Profundo. Todos los
          derechos reservados.
        </p>

        <div className="flex flex-wrap justify-center gap-6 font-lato text-sm" data-id="element-24">
          <button onClick={() => openModal('privacy')} className="text-cream/60 hover:text-gold-300 transition-colors cursor-pointer underline decoration-transparent hover:decoration-gold-300 underline-offset-4" data-id="element-25">
            Política de Privacidad
          </button>
          <button onClick={() => openModal('terms')} className="text-cream/60 hover:text-gold-300 transition-colors cursor-pointer underline decoration-transparent hover:decoration-gold-300 underline-offset-4" data-id="element-26">
            Términos y Condiciones
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-27">
        {activeModal && <Modal type={activeModal} onClose={closeModal} data-id="element-28" />}
      </AnimatePresence>
    </footer>;
}
function Modal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-29">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" data-id="element-30" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-2xl bg-navy-800 border border-gold-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]" data-id="element-31">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gold-500/10 bg-navy-900/50" data-id="element-32">
          <h3 className="font-playfair text-2xl font-bold text-gold-300" data-id="element-33">
            {title}
          </h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-cream/60 hover:text-cream transition-colors" data-id="element-34">
            <X size={20} data-id="element-35" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar text-cream/80 font-lato leading-relaxed space-y-4 text-sm md:text-base" data-id="element-36">
          {type === 'privacy' ? <PrivacyContent data-id="element-37" /> : <TermsContent data-id="element-38" />}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gold-500/10 bg-navy-900/30 text-center" data-id="element-39">
          <button onClick={onClose} className="px-6 py-2 rounded-full border border-gold-500/30 text-gold-300 hover:bg-gold-500/10 transition-colors font-bold text-sm" data-id="element-40">
            Cerrar
          </button>
        </div>
      </motion.div>
    </div>;
}
function PrivacyContent() {
  return <>
      <p data-id="element-41">
        <strong data-id="element-42">Última actualización: {new Date().toLocaleDateString()}</strong>
      </p>
      <p data-id="element-43">
        Su privacidad es importante para nosotros. Es política de El Método
        Sueño Profundo respetar su privacidad con respecto a cualquier
        información que podamos recopilar de usted a través de nuestro sitio
        web.
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-44">
        1. Información que recopilamos
      </h4>
      <p data-id="element-45">
        Solo solicitamos información personal cuando realmente la necesitamos
        para brindarle un servicio. La recopilamos por medios justos y legales,
        con su conocimiento y consentimiento. También le informamos por qué la
        recopilamos y cómo se utilizará.
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-46">
        2. Uso de la información
      </h4>
      <p data-id="element-47">
        Retenemos la información recopilada solo durante el tiempo que sea
        necesario para brindarle el servicio solicitado. Los datos que
        almacenamos, los protegeremos dentro de medios comercialmente aceptables
        para evitar pérdidas y robos, así como el acceso, divulgación, copia,
        uso o modificación no autorizados.
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-48">3. Cookies</h4>
      <p data-id="element-49">
        Utilizamos "cookies" para recopilar información sobre usted y su
        actividad en nuestro sitio. Una cookie es un pequeño fragmento de datos
        que nuestro sitio web almacena en su computadora y accede cada vez que
        lo visita, para que podamos comprender cómo utiliza nuestro sitio.
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-50">4. Terceros</h4>
      <p data-id="element-51">
        No compartimos ninguna información de identificación personal
        públicamente o con terceros, excepto cuando lo exige la ley.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p data-id="element-52">
        <strong data-id="element-53">Última actualización: {new Date().toLocaleDateString()}</strong>
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-54">
        1. Aceptación de los Términos
      </h4>
      <p data-id="element-55">
        Al acceder al sitio web de El Método Sueño Profundo, usted acepta estar
        vinculado por estos términos de servicio, todas las leyes y regulaciones
        aplicables, y acepta que es responsable del cumplimiento de cualquier
        ley local aplicable.
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-56">
        2. Licencia de Uso
      </h4>
      <p data-id="element-57">
        Se concede permiso para descargar temporalmente una copia de los
        materiales (información o software) en el sitio web de El Método Sueño
        Profundo solo para visualización transitoria personal y no comercial.
        Esta es la concesión de una licencia, no una transferencia de título.
      </p>
      <p className="mt-2" data-id="element-58">Bajo esta licencia, no puede:</p>
      <ul className="list-disc pl-5 space-y-1 mt-2" data-id="element-59">
        <li data-id="element-60">Modificar o copiar los materiales;</li>
        <li data-id="element-61">
          Utilizar los materiales para cualquier propósito comercial o para
          cualquier exhibición pública;
        </li>
        <li data-id="element-62">
          Intentar descompilar o realizar ingeniería inversa de cualquier
          software contenido en el sitio web;
        </li>
        <li data-id="element-63">
          Eliminar cualquier copyright u otras notaciones de propiedad de los
          materiales; o
        </li>
        <li data-id="element-64">
          Transferir los materiales a otra persona o "reflejar" los materiales
          en cualquier otro servidor.
        </li>
      </ul>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-65">
        3. Descargo de Responsabilidad
      </h4>
      <p data-id="element-66">
        Los materiales en el sitio web de El Método Sueño Profundo se
        proporcionan "tal cual". El Método Sueño Profundo no ofrece garantías,
        expresas o implícitas, y por la presente renuncia y niega todas las
        demás garantías, incluidas, entre otras, las garantías implícitas o las
        condiciones de comerciabilidad, idoneidad para un propósito particular o
        no infracción de la propiedad intelectual u otra violación de derechos.
      </p>

      <h4 className="text-gold-200 font-bold text-lg mt-4 mb-2" data-id="element-67">
        4. Limitaciones
      </h4>
      <p data-id="element-68">
        En ningún caso El Método Sueño Profundo o sus proveedores serán
        responsables de ningún daño (incluidos, entre otros, daños por pérdida
        de datos o ganancias, o debido a la interrupción del negocio) que surjan
        del uso o la imposibilidad de usar los materiales en el sitio web.
      </p>
    </>;
}